import {
  canIdentifyBy,
  CREATED_IN_CIO_AT,
  enabledIdentifiers,
  IdentifierKey,
  isEmailIdentified,
  isMultiIdentifier,
  PERMANENT_ATTRIBUTES,
  RESTRICTED_ATTRIBUTES,
  type AttributeRow,
  type AttributeValidationRules,
  type IdentifiersConfig,
} from '@/features/people';
import type { PersonAttributesEnvironment } from '@/features/people';

/**
 * The rows a new person starts with: a blank row per workspace identifier
 * (plus `email` on id-identified workspaces, so the profile has a contact
 * address), and `created_at` set to now. Ordered like a profile's attribute
 * list: a promoted `phone` first, then the reserved names in their fixed order.
 */
export function buildInitialAttributeRows(
  identifiers: IdentifiersConfig | null | undefined,
  nowUnixSeconds: number,
): AttributeRow[] {
  const values = new Map<string, string>();
  for (const identifier of enabledIdentifiers(identifiers)) {
    values.set(identifier, '');
  }
  if (canIdentifyBy(IdentifierKey.Id, identifiers)) {
    values.set(IdentifierKey.Email, '');
  }
  values.set('created_at', String(nowUnixSeconds));

  const leading: string[] = canIdentifyBy(IdentifierKey.Phone, identifiers) ? [IdentifierKey.Phone] : [];
  const custom = [...values.keys()]
    .filter((key) => !leading.includes(key) && !RESTRICTED_ATTRIBUTES.includes(key))
    .sort();
  const ordered = [...new Set([...leading, ...RESTRICTED_ATTRIBUTES, ...custom])].filter(
    (key) => values.has(key) && key !== IdentifierKey.CioId,
  );

  return ordered.map((key) => ({ key, value: values.get(key) ?? '', originalKey: key }));
}

/**
 * The naming rules for a new person's attributes: the identifiers are the
 * required names; every reserved name is taken unless the workspace uses
 * multiple identifiers; and `id` joins the backend-assigned names when the
 * workspace identifies people by email.
 */
export function newPersonValidationRules(
  environment: Pick<PersonAttributesEnvironment, 'identifier_type' | 'identifiers'>,
): AttributeValidationRules {
  return {
    requiredAttributes: enabledIdentifiers(environment.identifiers),
    permanentAttributes: isMultiIdentifier(environment) ? [] : PERMANENT_ATTRIBUTES,
    autoGeneratedAttributes: isEmailIdentified(environment)
      ? [IdentifierKey.CioId, IdentifierKey.Id, CREATED_IN_CIO_AT]
      : [IdentifierKey.CioId, CREATED_IN_CIO_AT],
  };
}
